import React from "react";

interface TimeProps {
  timestamp: string | Date | number | undefined;
  style?: React.CSSProperties;
}

const Time: React.FC<TimeProps> = ({ timestamp, style = {} }) => {
  const getRelativeTime = (timestamp?: string | Date | number) => {
    if (!timestamp) return "Invalid time"; // Handle undefined case

    const currentTime = new Date(); // Local time
    
    // Parse timestamp correctly whether it's a string, number, or Date
    let inputTime: Date;

    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      inputTime = new Date(timestamp);
    } else if (timestamp instanceof Date) {
      inputTime = timestamp;
    } else {
      return "Invalid time";
    }

    // Ensure the timestamp is valid
    if (isNaN(inputTime.getTime())) {
      return "Invalid time";
    }

    // Calculate the time difference in seconds
    const timeDifferenceInSeconds = Math.floor((currentTime.getTime() - inputTime.getTime()) / 1000);

    // Handle future dates
    if (timeDifferenceInSeconds < 0) {
      return "Just now";
    }

    // Display relative time logic
    if (timeDifferenceInSeconds < 60) {
      return `${timeDifferenceInSeconds} second${timeDifferenceInSeconds !== 1 ? 's' : ''} ago`;
    }

    const minutes = Math.floor(timeDifferenceInSeconds / 60);
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }

    const years = Math.floor(days / 365);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  return <span style={style}>{getRelativeTime(timestamp)}</span>;
};

export default Time;
